<template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="isDark ? 'dark' : 'light'"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { GridApi, GridOptions } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';
import ProgressCell from './cells/ProgressCell.vue';
import TrackingActions from './cells/TrackingActions.vue';
import { useParticipations } from '@/queries/participant.query.js';
import { ParticipantRow } from '@/types/participant.js';
import ProgressFilter from './filters/ProgressFilter.vue';

const { locale, t } = useI18n();
const isDark = useDark();
const { data: participations, isPending } = useParticipations();

let gridApi: GridApi;

const noRowsTemplate = ref(`<div>${t('app.tables.trackingTable.noRowTemplate')}</div>`);

const emit = defineEmits<{
  (e: 'onDesactivate', id: string): void,
  (e: 'onActivate', id: string): void,
  (e: 'onDelete', id: string): void,
  (e: 'onMove', id: string): void,
  (e: 'onRemind', id: string): void,
}>();

const props = withDefaults(defineProps<{
  search: {
    value: string,
    searchable: any[],
  },
  filters: any,
}>(), {
  search: () => {
    return {
      value: "",
      searchable: [
        "name",
      ],
    }
  }
});

const options = computed(() => {
  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
    }),
    scrollbarWidth: 12,
    suppressCellFocus: true,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
    },
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: [
      { field: 'talent', headerName: t('other.table.columns.participant'), flex: 1 },
      { field: 'email', headerName: t('other.table.columns.email'), flex: 1 },
      { field: 'date', headerName: t('other.table.columns.invitedAt'), flex: 0,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return new Intl.DateTimeFormat(locale.value, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(new Date(params.value));
        },
      },
      {
        headerName: t('other.table.columns.progress'),
        field: 'progress',
        sortable: false,
        flex: 1,
        headerClass: 'header-center',
        filter: ProgressFilter,
        cellRenderer: ProgressCell,
        valueFormatter: params => {
          return params.value?.progress?.toString() ?? '';
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        flex: 0,
        headerClass: 'header-right',
        cellRenderer: TrackingActions,
        cellRendererParams: {
          onDesactivate: (row: ParticipantRow) => emit('onDesactivate', row.id),
          onActivate: (row: ParticipantRow) => emit('onActivate', row.id),
          onDelete: (row: ParticipantRow) => emit('onDelete', row.id),
          onMove: (row: ParticipantRow) => emit('onMove', row.id),
          onRemind: (row: ParticipantRow) => emit('onRemind', row.id),
        }
      },
    ]
  } as GridOptions<ParticipantRow>;
});

const filteredParticipants = computed(() => {
  if (isPending.value) return [];
    const participantsCopy = [...participations.value];
    return participantsCopy.sort((pA, pB) => {
      const pAState = pA.state;
      const pBState = pB.state;

      if (pAState > pBState) return 1;
      if (pAState < pBState) return -1;
      if (pA.activated && !pB.activated) return 1;
      if (pAState === 1 && pBState === 1) {
        return pA.progress - pB.progress;
      }
      return dateIsBefore(pA.creation_time, pB.creation_time) ? 1 : -1;
    });
});

const rowData = computed(() => {
  return filteredParticipants.value.map((p) => {
    return {
      id: p.participant_id,
      talent: `${p?.candidate?.first_name || p?.pending_first_name || ""} ${p?.candidate?.last_name || p?.pending_first_name || ""}`,
      email: p?.candidate?.email || p?.pending_email || "",
      date: p?.creation_time,
      progress: {
        state: p.state,
        activated: p.activated,
        progress: p.progress,
        isCreated: !!p.candidate,
      },
    } as ParticipantRow;
  })
});

function onGridReady(params) {
  gridApi = params.api
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['actions']);
  gridApi.autoSizeColumns(['date']);
}
</script>

<style lang="scss">
@import "@/assets/style/variables.scss";

.ag-row {
  cursor: pointer;
}

.ag-body-vertical-scroll-viewport::-webkit-scrollbar {
  display: block;
  width: 12px;
}

.ag-body-vertical-scroll-viewport::-webkit-scrollbar-track {
  background: var(--light-grey-color);
}

.ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb {
  background: $primary-color;
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: content-box;
}

.header-center .ag-header-cell-label {
  justify-content: center;
}

.header-right .ag-header-cell-label {
  justify-content: flex-end;
}
</style>